import _ from 'lodash';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AiPreferencesTab } from '@/core/AiPreferencesTab';
import { SaveDatasourcePreferencesTab } from '@/core/SaveDatasourcePreferencesTab';
import { sqWorkbenchStore, sqWorkbookStore } from '@/core/core.stores';
import { useFlux } from '@/core/hooks/useFlux.hook';
import { useFluxPath } from '@/core/hooks/useFluxPath.hook';
import { SimpleSaveFormBuilder } from '@/formbuilder/SimpleSaveFormBuilder.page';
import { EditUserProfile } from '@/header/EditUserProfile.atom';
import { isAdministration, isLogs, onHomeScreen } from '@/main/routing.utilities';
import { sqUsersApi } from '@/sdk/api/UsersApi';
import { setDatasources } from '@/search/search.actions';
import { sqTimezones } from '@/utilities/datetime.constants';
import { LOCALES, LOCALE_DATA } from '@/utilities/i18n.constants';
import { switchLanguage } from '@/utilities/i18n.utilities';
import { errorToast, successToast } from '@/utilities/toast.utilities';
import { switchModeAndTheme } from '@/utilities/utilities';
import {
  setCurrentUser,
  setDarkMode,
  setPreferNewTab,
  setUserPreferencesDisplay,
  setUserPreferencesTab,
  setUserTimeZone,
} from '@/workbench/workbench.actions';
import { PREFERENCE_TABS } from '@/workbench/workbench.store';
import { Modal, Tabs } from '@seeqdev/qomponents';
import type { FormElement } from '@/formbuilder/formBuilder.constants';
import type { UserInputV1 } from '@/sdk/model/UserInputV1';
import type { LocaleKey } from '@/utilities/i18n.constants';
import type { themeType } from '@/utilities/utilities';

interface EditUserPreferencesProps {
  closeFn: () => void;
}

interface updateUserValues {
  firstName: string;
  lastName: string;
  email: string;
  timezone: any;
  language: { text: string; value: LocaleKey };
  preferNewTab: boolean;
  userDarkMode: boolean;
}

export const EditUserPreferencesModal = ({ closeFn }: EditUserPreferencesProps) => {
  const { t } = useTranslation();

  const { currentUser: user, userTimeZone, userLanguage, preferNewTab, darkMode } = sqWorkbenchStore;

  const doUseDarkMode = useRef(darkMode);
  const isCancelEnabled = userTimeZone;
  const { userPreferencesTab } = useFlux(sqWorkbenchStore);
  const formattedLanguageOptions = _.map(LOCALE_DATA, (language) => ({
    text: language.text,
    value: language.key,
  }));
  const isVantage = useFluxPath(sqWorkbookStore, () => sqWorkbookStore.isVantage);
  const isReport = useFluxPath(sqWorkbookStore, () => sqWorkbookStore.isTopic);

  const selectedLanguage = _.find(formattedLanguageOptions, {
    value: userLanguage,
  });

  let theme = 'analysis';
  if (onHomeScreen() || isLogs() || isAdministration() || isReport) {
    theme = 'topic';
  } else if (isVantage) {
    theme = 'vantage';
  }

  useEffect(() => {
    setDatasources('main');
  }, []);

  const currentUserMissingInfo = sqWorkbenchStore.isCurrentUserMissingInfo();

  const updateUser = (values: updateUserValues) => {
    const { firstName, lastName, email, timezone, language, preferNewTab } = values;
    const name = `${_.trim(firstName)} ${_.trim(lastName)}`;
    const userProps = { email, firstName, lastName, name };

    if (darkMode !== values.userDarkMode) {
      setDarkMode(values.userDarkMode);
    }

    return sqUsersApi
      .updateUser(userProps as UserInputV1, { id: user.id })
      .then(() => setCurrentUser())
      .then(() => setUserTimeZone(timezone))
      .then(() => setPreferNewTab(preferNewTab))
      .then(() => switchLanguage(language?.value ?? LOCALES.EN))
      .then(() => successToast({ messageKey: 'USER.PREFERENCES_UPDATED' }))
      .then(() => (currentUserMissingInfo ? setUserPreferencesTab(PREFERENCE_TABS.PROFILE) : closeFn()))
      .catch((error) => errorToast({ httpResponseOrError: error }));
  };

  const formDefinition: FormElement[] = [
    {
      component: 'FormGroup',
      name: 'userPreferences',
      components: [
        {
          component: 'TimeZoneSelectorFormComponent',
          name: 'timezone',
          label: 'USER.TIME_ZONE',
          value: userTimeZone || sqTimezones.defaultTimezone,
          onChange: _.noop,
          defaultTimeZone: sqTimezones.defaultTimezone,
          skipToFormState: true,
          testId: 'timezone',
        },
        {
          component: 'IconSelectFormComponent',
          name: 'language',
          label: 'USER.LANGUAGE.SELECTION',
          value: selectedLanguage,
          onChange: _.noop,
          selectOptions: formattedLanguageOptions,
          insideModal: true,
          skipToFormState: true,
          testId: 'language',
        },
        {
          component: 'CheckboxFormComponent',
          id: 'userPreferNewTab',
          name: 'preferNewTab',
          label: '',
          onChange: _.noop,
          skipToFormState: true,
          checkboxLabel: 'USER.PREFER_NEW_TAB',
          value: preferNewTab,
        },
        {
          component: 'CheckboxFormComponent',
          id: 'userDarkMode',
          name: 'userDarkMode',
          label: '',
          onChange: () => {
            switchModeAndTheme(!doUseDarkMode.current, theme as themeType);
            doUseDarkMode.current = !doUseDarkMode.current;
          },
          skipToFormState: true,
          checkboxLabel: 'DARK_MODE.ENABLE',
          value: doUseDarkMode.current,
        },
      ],
    },
  ];

  const closeAndReset = () => {
    // if a user clicks the cancel button we need to make sure that we
    // toggle the darkmode display back to the intended display
    if (doUseDarkMode.current !== darkMode) {
      switchModeAndTheme(darkMode, theme as themeType);
    }
    closeFn();
  };

  return (
    // The close and cancel buttons get hidden when the user has not set their timezone to require the user
    // to save their preferences, which will set their timezone
    <Modal
      open={true}
      onSubmit={_.noop}
      onClose={closeAndReset}
      data-testid="editUserPreferences"
      hideCloseIcon={!isCancelEnabled}
      title={t('EDIT_PREFERENCES')}
      hideFooterButtons={true}>
      <Tabs
        testId="userPreferencesTabs"
        extraClassNames="width-maximum"
        activeTab={userPreferencesTab}
        defaultActiveTab={currentUserMissingInfo ? PREFERENCE_TABS.PROFILE : PREFERENCE_TABS.SYSTEM}
        onTabSelect={(tabIndex) => {
          if (typeof tabIndex === 'string') {
            setUserPreferencesTab(tabIndex);
          }
        }}
        tabs={[
          {
            id: PREFERENCE_TABS.PROFILE,
            testId: 'userProfilePreferencesTab',
            label: t('USER.PROFILE_PREFERENCES'),
            tabContentExtraClassNames: 'link-no-underline tw:h-full',
            tabExtraClassNames: 'link-no-underline',
            content: (
              <div className="pt10" data-testid="editUserPreferences">
                <EditUserProfile
                  closeFn={() => {
                    setUserPreferencesDisplay(false);
                    closeFn();
                  }}
                />
              </div>
            ),
          },
          {
            id: PREFERENCE_TABS.SYSTEM,
            label: t('USER.SYSTEM_PREFERENCES'),
            tabContentExtraClassNames: 'link-no-underline tw:h-full',
            tabExtraClassNames: 'link-no-underline',
            testId: 'systemPreferencesTab',
            content: (
              <div className="pt10" data-testid="editUserPreferences">
                <SimpleSaveFormBuilder
                  formDefinition={formDefinition}
                  submitFn={updateUser}
                  submitBtnLabel={currentUserMissingInfo ? 'NEXT' : 'SAVE'}
                  closeFn={closeAndReset}
                  hideCancel={!isCancelEnabled}
                />
              </div>
            ),
          },
          {
            id: PREFERENCE_TABS.DATASOURCES,
            label: t('USER.DATASOURCE_PREFERENCES'),
            tabContentExtraClassNames: 'link-no-underline  tw:h-full',
            tabExtraClassNames: 'link-no-underline',
            testId: 'userDatasourcePreferencesTab',
            content: (
              <div className="pt10" data-testid="editUserDatasourcePreferences">
                <SaveDatasourcePreferencesTab />
              </div>
            ),
          },
          // AI Preferences Tab
          {
            id: PREFERENCE_TABS.AI,
            label: t('USER.AI_PREFERENCES'),
            testId: 'aiPreferencesTab',
            tabContentExtraClassNames: 'link-no-underline  tw:h-full',
            content: (
              <div className="pt10" data-testid="editUserAiPreferences">
                <AiPreferencesTab />
              </div>
            ),
          },
        ]}
      />
    </Modal>
  );
};
